import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Landing from "./App";
import { AuthProvider } from "./lms/contexts/AuthContext";
import RoleRoute from "./lms/components/RoleRoute";

import LoginPage from "./lms/pages/LoginPage";
import ForgotPasswordPage from "./lms/pages/ForgotPasswordPage";
import RegisterPage from "./lms/pages/RegisterPage";
import ApplyPage from "./lms/pages/ApplyPage";
import FreeBatchApplyPage from "./lms/pages/FreeBatchApplyPage";
import ChangePasswordPage from "./lms/pages/ChangePasswordPage";
import ResetPasswordPage from "./lms/pages/ResetPasswordPage";

import StudentLayout from "./lms/components/StudentLayout";
import StudentHome from "./lms/pages/StudentHome";
import StudentStudy from "./lms/pages/StudentStudy";
import SettingsPage from "./lms/pages/SettingsPage";
import SecurityPage from "./lms/pages/SecurityPage";
import SessionKickedModal from "./lms/components/SessionKickedModal";

// ── Secondary student pages — lazy-loaded so the initial student bundle
//    stays lean. Saves ~700KB+ off the eager main.js chunk and reduces
//    Time-to-Interactive for the most common landing pages (Home, Study).
const StudentLecture = lazy(() => import("./lms/pages/StudentLecture"));
const StudentQuiz = lazy(() => import("./lms/pages/StudentQuiz"));
const StudentRevision = lazy(() => import("./lms/pages/StudentRevision"));
const StudentLeaderboard = lazy(() => import("./lms/pages/StudentLeaderboard"));
const StudentFlashcards = lazy(() => import("./lms/pages/StudentFlashcards"));
const StudentMentor = lazy(() => import("./lms/pages/StudentMentor"));
const StudentCommunity = lazy(() => import("./lms/pages/StudentCommunity"));
const StudentDiscussion = lazy(() => import("./lms/pages/StudentDiscussion"));
const StudentDiscussionRoom = lazy(() => import("./lms/pages/StudentDiscussionRoom"));
const StudentSavedConcepts = lazy(() => import("./lms/pages/StudentSavedConcepts"));
const StudentProfile = lazy(() => import("./lms/pages/StudentProfile"));
const StudentStreak = lazy(() => import("./lms/pages/StudentStreak"));
const StreakLeaderboard = lazy(() => import("./lms/pages/StreakLeaderboard"));
const MentorPortal = lazy(() => import("./lms/pages/MentorPortal"));

// ── Admin pages — fully lazy. The vast majority of sessions are students,
//    so paying the cost of ~25 admin pages upfront made no sense.
const AdminLayout = lazy(() => import("./lms/components/AdminLayout"));
const AdminOverview = lazy(() => import("./lms/pages/AdminOverview"));
const AdminRegistrations = lazy(() => import("./lms/pages/AdminRegistrations"));
const AdminBatches = lazy(() => import("./lms/pages/AdminBatches"));
const AdminBanners = lazy(() => import("./lms/pages/AdminBanners"));
const AdminContent = lazy(() => import("./lms/pages/AdminContent"));
const AdminMCQs = lazy(() => import("./lms/pages/AdminMCQs"));
const AdminQuickTakes = lazy(() => import("./lms/pages/AdminQuickTakes"));
const AdminMentors = lazy(() => import("./lms/pages/AdminMentors"));
const AdminMentorOversight = lazy(() => import("./lms/pages/AdminMentorOversight"));
const AdminReports = lazy(() => import("./lms/pages/AdminReports"));
const AdminStudents = lazy(() => import("./lms/pages/AdminStudents"));
const AdminStudentsGlobal = lazy(() => import("./lms/pages/AdminStudentsGlobal"));
const AdminNotifications = lazy(() => import("./lms/pages/AdminNotifications"));
const AdminBulkEnroll = lazy(() => import("./lms/pages/AdminBulkEnroll"));
const AdminBatchCurriculum = lazy(() => import("./lms/pages/AdminBatchCurriculum"));
const AdminTeam = lazy(() => import("./lms/pages/AdminTeam"));
const AdminBulkEmail = lazy(() => import("./lms/pages/AdminBulkEmail"));
const AdminTeamRoles = lazy(() => import("./lms/pages/AdminTeamRoles"));
const AdminDiscussionOversight = lazy(() => import("./lms/pages/AdminDiscussionOversight"));
const AdminSecurity = lazy(() => import("./lms/pages/AdminSecurity"));
const AdminCompete = lazy(() => import("./lms/pages/AdminCompete"));
const AdminCompeteEvent = lazy(() => import("./lms/pages/AdminCompeteEvent"));
const AdminScholarships = lazy(() => import("./lms/pages/AdminScholarships"));
const AdminScholarshipEditor = lazy(() => import("./lms/pages/AdminScholarships").then(m => ({ default: m.AdminScholarshipEditor })));
const AdminScholarshipApplications = lazy(() => import("./lms/pages/AdminScholarships").then(m => ({ default: m.AdminScholarshipApplications })));
const AdminLeaderboards = lazy(() => import("./lms/pages/AdminLeaderboards"));
const AdminDiscussionContributors = lazy(() => import("./lms/pages/AdminDiscussionContributors"));
const AdminDiscussionRooms = lazy(() => import("./lms/pages/AdminDiscussionRooms"));
const AdminSiteSettings = lazy(() => import("./lms/pages/AdminSiteSettings"));
const AdminMigrations = lazy(() => import("./lms/pages/AdminMigrations"));
const AdminStreakDashboard = lazy(() => import("./lms/pages/AdminStreakDashboard"));

// ── Compete pages — lazy-loaded so the LMS landing/student bundle stays lean ──
const CompeteLanding = lazy(() => import("./compete/CompeteLanding"));
const CompeteEventPage = lazy(() => import("./compete/CompeteEventPage"));
const CompeteLogin = lazy(() => import("./compete/CompeteDashboard").then(m => ({ default: m.CompeteLogin })));
const CompeteLayout = lazy(() => import("./compete/CompeteDashboard").then(m => ({ default: m.CompeteLayout })));
const CompeteHome = lazy(() => import("./compete/CompeteDashboard").then(m => ({ default: m.CompeteHome })));
const CompeteProfile = lazy(() => import("./compete/CompeteDashboard").then(m => ({ default: m.CompeteProfile })));
const CompeteCommunity = lazy(() => import("./compete/CompeteDashboard").then(m => ({ default: m.CompeteCommunity })));
const CompeteTestRunner = lazy(() => import("./compete/CompeteTestRunner"));
const CompeteResult = lazy(() => import("./compete/CompeteTestRunner").then(m => ({ default: m.CompeteResult })));
const CompeteLeaderboard = lazy(() => import("./compete/CompeteLeaderboard").then(m => ({ default: m.CompeteLeaderboard })));
const CompeteCertificate = lazy(() => import("./compete/CompeteLeaderboard").then(m => ({ default: m.CompeteCertificate })));
const CompeteDiscussions = lazy(() => import("./compete/CompeteLeaderboard").then(m => ({ default: m.CompeteDiscussions })));
const CompeteHallOfFame = lazy(() => import("./compete/CompeteHallOfFame"));
const CompeteReferrals = lazy(() => import("./compete/CompeteReferrals"));
const ScholarshipApply = lazy(() => import("./compete/ScholarshipApply"));
const ScholarshipMyApplications = lazy(() => import("./compete/ScholarshipApply").then(m => ({ default: m.ScholarshipMyApplications })));

// Tiny suspense fallback — matches the Compete brand
const CompeteFallback = () => (
  <div className="portal-compete min-h-screen flex items-center justify-center"
    style={{ background: "var(--px-paper)" }}>
    <div className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-semibold"
      style={{ color: "var(--px-ink-mute)", fontFamily: "var(--font-mono)" }}>
      <span className="inline-block w-3 h-3 rounded-full animate-pulse"
        style={{ background: "var(--accent)" }} />
      Loading
    </div>
  </div>
);
const SC = (Comp) => (
  <Suspense fallback={<CompeteFallback />}><Comp /></Suspense>
);

export default function Root() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SessionKickedModal />
        <Toaster
          theme="dark"
          position="top-center"
          richColors
          closeButton
          toastOptions={{
            style: {
              background: "rgba(6,18,38,0.96)",
              border: "1px solid rgba(92,238,255,0.25)",
              color: "#fff",
              backdropFilter: "blur(18px)",
            },
          }}
        />
        <Suspense fallback={<CompeteFallback />}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/register/:slug" element={<RegisterPage />} />
          {/* Iter 161 · Phase 2 — public "Apply Now" for a 100% free batch */}
          <Route path="/free/:slug" element={<FreeBatchApplyPage />} />

          {/* LogicsWallah Compete — public */}
          <Route path="/test" element={SC(CompeteLanding)} />
          <Route path="/test/login" element={SC(CompeteLogin)} />
          <Route path="/test/hall-of-fame" element={SC(CompeteHallOfFame)} />
          <Route path="/test/hall-of-fame/:slug" element={SC(CompeteHallOfFame)} />
          <Route path="/test/:slug" element={SC(CompeteEventPage)} />

          {/* LogicsWallah Compete — authenticated student dashboard */}
          <Route path="/test/dashboard" element={SC(CompeteLayout)}>
            <Route index element={SC(CompeteHome)} />
            <Route path="profile" element={SC(CompeteProfile)} />
            <Route path="community" element={SC(CompeteCommunity)} />
            <Route path="take" element={SC(CompeteTestRunner)} />
            <Route path="result" element={SC(CompeteResult)} />
            <Route path="leaderboard" element={SC(CompeteLeaderboard)} />
            <Route path="certificate" element={SC(CompeteCertificate)} />
            <Route path="discussions" element={SC(CompeteDiscussions)} />
            <Route path="referrals" element={SC(CompeteReferrals)} />
            <Route path="scholarships" element={SC(ScholarshipMyApplications)} />
            <Route path="scholarships/:scholarshipId/apply" element={SC(ScholarshipApply)} />
          </Route>

          {/* Forced first-login password change — any authenticated role */}
          <Route
            path="/change-password"
            element={
              <RoleRoute allow={["admin", "student", "mentor"]}>
                <ChangePasswordPage />
              </RoleRoute>
            }
          />

          {/* Student portal */}
          <Route
            path="/student"
            element={
              <RoleRoute allow={["student"]}>
                <StudentLayout />
              </RoleRoute>
            }
          >
            <Route index element={<StudentHome />} />
            <Route path="study" element={<StudentStudy />} />
            <Route path="lecture/:id" element={<StudentLecture />} />
            <Route path="quiz" element={<StudentQuiz />} />
            <Route path="tests" element={<StudentQuiz />} />
            <Route path="revision" element={<StudentRevision />} />
            <Route path="flashcards" element={<StudentFlashcards />} />
            <Route path="leaderboard" element={<StudentLeaderboard />} />
            <Route path="mentor" element={<StudentMentor />} />
            <Route path="community" element={<StudentCommunity />} />
            <Route path="discussion" element={<StudentDiscussion />} />
            <Route path="discussion/saved" element={<StudentSavedConcepts />} />
            <Route path="discussion/:slug" element={<StudentDiscussionRoom />} />
            <Route path="profile" element={<StudentProfile />} />
            <Route path="streak" element={<StudentStreak />} />
            <Route path="streak-leaderboard" element={<StreakLeaderboard />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/security" element={<SecurityPage />} />
            {/* Iter 158: catch-all inside the /student parent so a frozen
                student who deep-links into a stale/non-existent sub-path
                (e.g. /student/dashboard) still lands on the FrozenScreen
                via RoleRoute, instead of bouncing to the marketing landing. */}
            <Route path="*" element={<StudentHome />} />
          </Route>
          <Route
            path="/admin"
            element={
              <RoleRoute allow={["admin"]}>
                <AdminLayout />
              </RoleRoute>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="registrations" element={<AdminRegistrations />} />
            <Route path="batches" element={<AdminBatches />} />
            <Route path="bulk-enroll/:batchId" element={<AdminBulkEnroll />} />
            <Route path="batches/:batchId/curriculum" element={<AdminBatchCurriculum />} />
            <Route path="banners" element={<AdminBanners />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="students-global" element={<AdminStudentsGlobal />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="mcqs" element={<AdminMCQs />} />
            <Route path="quick-takes" element={<AdminQuickTakes />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="leaderboards" element={<AdminLeaderboards />} />
            <Route path="discussion-contributors" element={<AdminDiscussionContributors />} />
            <Route path="discussion-rooms" element={<AdminDiscussionRooms />} />
            <Route path="mentors" element={<AdminMentors />} />
            <Route path="mentor-oversight" element={<AdminMentorOversight />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="compete" element={<AdminCompete />} />
            <Route path="compete/:id" element={<AdminCompeteEvent />} />
            <Route path="compete/:id/scholarships" element={<AdminScholarships />} />
            <Route path="compete/:id/scholarships/:scholarshipId" element={<AdminScholarshipEditor />} />
            <Route path="compete/:id/scholarships/:scholarshipId/applications" element={<AdminScholarshipApplications />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="bulk-email" element={<AdminBulkEmail />} />
            <Route path="team-roles" element={<AdminTeamRoles />} />
            <Route path="discussion/oversight" element={<AdminDiscussionOversight />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="migrations" element={<AdminMigrations />} />
            <Route path="streak" element={<AdminStreakDashboard />} />
            <Route path="site-settings" element={<AdminSiteSettings />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Mentor portal */}
          <Route
            path="/mentor"
            element={
              <RoleRoute allow={["mentor"]}>
                <MentorPortal />
              </RoleRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}
